import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { getAuth, isSuperAdmin, isSuperPfAdmin } from '../../lib/auth';
import { MainLayout } from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  CreditCard, Eye, ChevronLeft, ChevronRight, Loader2,
  CheckCircle2, Clock, DollarSign, FileText, ArrowLeft, AlertTriangle,
  RefreshCw, EyeOff, Send, MoreVertical, AlertOctagon, Upload, Download,
  Info, FileDown,
} from 'lucide-react';
import PaymentFileDetail from './PaymentFileDetail';

const formatCurrency = (amount) => {
  if (amount == null) return '\u2014';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  try { return format(new Date(dateStr), 'MMM d, yyyy'); } catch { return dateStr; }
};

const STATUS_CONFIG = {
  review: { label: 'In Review', color: 'bg-amber-500/15 text-amber-600 border-amber-500/20', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-500/15 text-blue-600 border-blue-500/20', icon: CheckCircle2 },
  paid: { label: 'Paid', color: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/20', icon: DollarSign },
};

const TABS = [
  { id: 'review', label: 'In Review' },
  { id: 'approved', label: 'Approved' },
  { id: 'paid', label: 'Paid' },
];

// ═══════════════════════════════════════════════════════════
// STATUS BADGE
// ═══════════════════════════════════════════════════════════
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.review;
  return (
    <Badge variant="outline" className={`${cfg.color} font-medium text-xs`} data-testid={`status-badge-${status}`}>
      {cfg.label}
    </Badge>
  );
};

// ═══════════════════════════════════════════════════════════
// PAYMENT FILE TABLE ROW
// ═══════════════════════════════════════════════════════════
const formatSyncTime = (dateStr) => {
  if (!dateStr) return null;
  try { return format(new Date(dateStr), 'MMM d, h:mm a'); } catch { return dateStr; }
};

const PFRow = ({ file, onView, onDownloadCsv, activeTab, stats, pmtStat, isAdmin, isPfAdmin, onResync, onResyncOverwrite, resyncingId }) => {
  const s = stats || {};
  const ps = pmtStat || {};
  const hasIssues = (s.total_issues || 0) > 0 && !(s.all_resolved);
  const canResync = (isAdmin || isPfAdmin);
  return (
  <TableRow className="hover:bg-muted/50 transition-colors" data-testid={`pf-row-${file.payment_file_id}`}>
    <TableCell className="font-medium text-sm">
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          {file.batch_name || `PF-${file.batch_id}`}
          {activeTab === 'review' && hasIssues && (
            <>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20 text-[10px] px-1.5 py-0" data-testid={`pf-issue-badge-${file.payment_file_id}`}>
                    <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />{s.total_issues}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs">
                  {s.duplicates > 0 && <p>{s.duplicates} duplicate(s)</p>}
                  {s.mismatches > 0 && <p>{s.mismatches} mismatch(es)</p>}
                  {s.dismissed > 0 && <p>{s.dismissed} dismissed</p>}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/20 text-[10px] px-1.5 py-0" data-testid={`pf-hidden-${file.payment_file_id}`}>
                    <EyeOff className="h-2.5 w-2.5 mr-0.5" />Hidden
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs">Resolve all issues to publish to Org users</TooltipContent>
              </Tooltip>
            </>
          )}
          {activeTab === 'review' && !hasIssues && s.invoice_count > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px] px-1.5 py-0" data-testid={`pf-published-${file.payment_file_id}`}>
                  <Send className="h-2.5 w-2.5 mr-0.5" />Published
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="text-xs">Visible to Org users (no unresolved issues)</TooltipContent>
            </Tooltip>
          )}
          {(activeTab === 'approved' || activeTab === 'paid') && ps.total_bills > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge
                  variant="outline"
                  className={`text-[10px] px-1.5 py-0 ${ps.paid_bills === ps.total_bills ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' : ps.paid_bills > 0 ? 'bg-blue-500/10 text-blue-600 border-blue-500/20' : 'bg-slate-500/10 text-slate-500 border-slate-500/20'}`}
                  data-testid={`pf-paid-stat-${file.payment_file_id}`}
                >
                  <DollarSign className="h-2.5 w-2.5 mr-0.5" />
                  {ps.paid_bills}/{ps.total_bills} Paid
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="text-xs">
                <p>{ps.paid_bills} of {ps.total_bills} bills paid ({ps.pct_paid}%)</p>
                <p>Total Paid: {formatCurrency(ps.total_paid)}</p>
                {(ps.total_to_refund > 0 || ps.total_refunded > 0) && (
                  <>
                    {ps.total_to_refund > 0 && <p className="text-amber-500">To Refund: {formatCurrency(ps.total_to_refund)}</p>}
                    {ps.total_refunded > 0 && <p className="text-emerald-600">Refunded: {formatCurrency(ps.total_refunded)}</p>}
                  </>
                )}
                {ps.total_svc_fee > 0 && <p>Service Fees: {formatCurrency(ps.total_svc_fee)}</p>}
                {ps.missing_txn_ref > 0 && <p className="text-amber-500">{ps.missing_txn_ref} missing txn ref</p>}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <span className="text-[10px] italic text-muted-foreground/70" data-testid={`pf-generated-${file.payment_file_id}`}>
          Generated {formatDate(file.generated_at)}
        </span>
      </div>
    </TableCell>
    <TableCell className="text-sm font-mono">{formatCurrency(file.total_requested_amount)}</TableCell>
    {(activeTab === 'paid' || activeTab === 'approved') && (
      <TableCell className="text-sm font-mono" data-testid={`pf-total-paid-${file.payment_file_id}`}>
        {formatCurrency(ps.total_paid || 0)}
      </TableCell>
    )}
    {activeTab === 'review' && (
      <TableCell className="text-sm text-muted-foreground">{formatDate(file.generated_at)}</TableCell>
    )}
    {activeTab === 'approved' && (
      <TableCell className="text-sm text-muted-foreground">{formatDate(file.approved_at)}</TableCell>
    )}
    {activeTab === 'paid' && (
      <TableCell className="text-sm text-muted-foreground">{formatDate(file.approved_at)}</TableCell>
    )}
    {activeTab === 'paid' && (
      <TableCell className="text-sm text-muted-foreground">{formatDate(file.paid_at)}</TableCell>
    )}
    <TableCell className="text-sm">
      <StatusBadge status={file.status} />
    </TableCell>
    <TableCell>
      <div className="flex flex-col items-start gap-0.5">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 text-xs"
            onClick={() => onView(file)}
            data-testid={`pf-view-${file.payment_file_id}`}
          >
            <Eye className="h-3.5 w-3.5 mr-1" /> View
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => onDownloadCsv(file)}
                data-testid={`pf-csv-${file.payment_file_id}`}
              >
                <FileDown className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="text-xs">Download as CSV</TooltipContent>
          </Tooltip>
          {canResync && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  data-testid={`pf-actions-menu-${file.payment_file_id}`}
                  disabled={resyncingId === file.payment_file_id}
                >
                  {resyncingId === file.payment_file_id ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <MoreVertical className="h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={() => onResync(file)}
                  disabled={resyncingId === file.payment_file_id}
                  data-testid={`pf-resync-${file.payment_file_id}`}
                  className="text-xs"
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-2" />
                  Re-sync
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onResyncOverwrite(file)}
                  disabled={resyncingId === file.payment_file_id}
                  data-testid={`pf-resync-overwrite-${file.payment_file_id}`}
                  className="text-xs text-destructive focus:text-destructive"
                >
                  <AlertOctagon className="h-3.5 w-3.5 mr-2" />
                  Sync & Overwrite
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {file.last_synced_at && (
          <span className="text-[10px] text-muted-foreground pl-1" data-testid={`pf-last-synced-${file.payment_file_id}`}>
            Synced: {formatSyncTime(file.last_synced_at)}
          </span>
        )}
      </div>
    </TableCell>
  </TableRow>
  );
};

// ═══════════════════════════════════════════════════════════
// PAYMENT FILES LIST TABLE
// ═══════════════════════════════════════════════════════════
const PFTable = ({ files, loading, activeTab, onView, onDownloadCsv, page, totalPages, onPageChange, issueStats, pmtStats, isAdmin, isPfAdmin, onResync, onResyncOverwrite, resyncingId }) => {
  if (loading) {
    return (
      <div className="space-y-3 p-4" data-testid="pf-table-loading">
        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }

  if (!files.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground" data-testid="pf-table-empty">
        <FileText className="h-12 w-12 mb-3 opacity-40" />
        <p className="text-sm">No payment files in this status</p>
      </div>
    );
  }

  return (
    <div>
      <Table data-testid="pf-table">
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className="text-xs font-semibold uppercase tracking-wider">Batch Name</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider">{(activeTab === 'approved' || activeTab === 'paid') ? 'Approved Amount' : 'Requested Amt'}</TableHead>
            {(activeTab === 'paid' || activeTab === 'approved') && (
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Paid</TableHead>
            )}
            {activeTab === 'review' && (
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Generated</TableHead>
            )}
            {activeTab === 'approved' && (
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Approved</TableHead>
            )}
            {activeTab === 'paid' && (
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Approved</TableHead>
            )}
            {activeTab === 'paid' && (
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Paid Date</TableHead>
            )}
            <TableHead className="text-xs font-semibold uppercase tracking-wider">Status</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider w-[180px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {files.map((f) => (
            <PFRow key={f.payment_file_id} file={f} onView={onView} onDownloadCsv={onDownloadCsv} activeTab={activeTab} stats={issueStats[f.payment_file_id]} pmtStat={pmtStats[f.payment_file_id]} isAdmin={isAdmin} isPfAdmin={isPfAdmin} onResync={onResync} onResyncOverwrite={onResyncOverwrite} resyncingId={resyncingId} />
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border px-4 py-3" data-testid="pf-pagination">
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════
export default function PaymentsV2Page() {
  const { user } = getAuth();
  const navigate = useNavigate();

  const [currentAccount, setCurrentAccount] = useState(() => {
    const str = localStorage.getItem('current_account');
    return str ? JSON.parse(str) : null;
  });

  useEffect(() => {
    const check = () => {
      const str = localStorage.getItem('current_account');
      const acc = str ? JSON.parse(str) : null;
      if (acc?.id && acc.id !== currentAccount?.id) setCurrentAccount(acc);
    };
    check();
    const iv = setInterval(check, 1000);
    const to = setTimeout(() => clearInterval(iv), 10000);
    return () => { clearInterval(iv); clearTimeout(to); };
  }, [currentAccount?.id]);

  const [activeTab, setActiveTab] = useState('review');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusCounts, setStatusCounts] = useState({ review: 0, approved: 0, paid: 0 });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [issueStats, setIssueStats] = useState({});
  const [pmtStats, setPmtStats] = useState({});
  const [resyncingId, setResyncingId] = useState(null);
  const [paymentStats, setPaymentStats] = useState(null);
  const [overwriteConfirmFile, setOverwriteConfirmFile] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkParsedRows, setBulkParsedRows] = useState([]);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkCommitting, setBulkCommitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [statsBreakdownOpen, setStatsBreakdownOpen] = useState(false);
  const limit = 20;
  const isAdmin = isSuperAdmin();
  const isPfAdmin = isSuperPfAdmin();

  // Detail view state
  const [selectedPF, setSelectedPF] = useState(null);

  const fetchFiles = useCallback(async () => {
    if (!currentAccount?.id) return;
    setLoading(true);
    try {
      // STEP 1: Fetch the PF list (fast, always first so we know IDs)
      const { data } = await api.get('/payments/v2/files', {
        params: { account_id: currentAccount.id, status: activeTab, page, limit },
      });
      const fetchedFiles = data.files || [];
      setTotal(data.total || 0);
      if (data.status_counts) setStatusCounts(data.status_counts);

      if (fetchedFiles.length === 0) {
        setFiles([]);
        setIssueStats({});
        setPmtStats({});
        return;
      }

      const pfIds = fetchedFiles.map(f => f.payment_file_id).join(',');

      // STEP 2: Parallelize issue-stats + payment-stats for approved/paid
      const needPmtStats = (activeTab === 'approved' || activeTab === 'paid');
      const [issueRes, pmtRes] = await Promise.all([
        api.get('/payments/v2/files/batch-issue-stats', {
          params: { account_id: currentAccount.id, pf_ids: pfIds },
        }).catch(() => ({ data: { stats: {} } })),
        needPmtStats
          ? api.get('/payments/v2/files/batch-payment-stats', {
              params: { account_id: currentAccount.id, pf_ids: pfIds },
            }).catch(() => ({ data: { stats: {} } }))
          : Promise.resolve({ data: { stats: {} } }),
      ]);

      const issueStatsData = issueRes.data.stats || {};
      setIssueStats(issueStatsData);
      setPmtStats(needPmtStats ? (pmtRes.data.stats || {}) : {});

      // RBAC: For "In Review" tab, non-super-admins only see PFs with all issues resolved
      if (activeTab === 'review' && !isAdmin) {
        const visible = fetchedFiles.filter(f => {
          const s = issueStatsData[f.payment_file_id];
          return !s || s.total_issues === 0 || s.all_resolved;
        });
        setFiles(visible);
        setTotal(visible.length);
      } else {
        setFiles(fetchedFiles);
      }
    } catch (err) {
      console.error('Failed to fetch payment files:', err);
      toast.error('Failed to load payment files');
    } finally {
      setLoading(false);
    }
  }, [currentAccount?.id, activeTab, page, isAdmin]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  // Fetch aggregate stats
  const fetchPaymentStats = useCallback(async () => {
    if (!currentAccount?.id) return;
    try {
      const { data } = await api.get('/payments/v2/stats', { params: { account_id: currentAccount.id } });
      setPaymentStats(data);
    } catch { /* silent */ }
  }, [currentAccount?.id]);

  useEffect(() => { fetchPaymentStats(); }, [fetchPaymentStats]);

  // Reset page when tab changes
  useEffect(() => { setPage(1); }, [activeTab]);

  const totalPages = Math.ceil(total / limit);

  const handleView = (file) => setSelectedPF(file);

  const handleBack = () => {
    setSelectedPF(null);
    fetchFiles();
    fetchPaymentStats();
  };

  const handleResync = async (file) => {
    setResyncingId(file.payment_file_id);
    try {
      const { data } = await api.post(`/payments/v2/files/${file.payment_file_id}/resync`, { force_overwrite: false }, {
        params: { account_id: currentAccount.id },
      });
      const totals = data?.sync_result?.totals || {};
      toast.success(`Re-sync completed for ${file.batch_name || file.payment_file_id} — ${totals.total_updated || 0} records updated`);
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-sync failed');
    } finally {
      setResyncingId(null);
    }
  };

  const handleResyncOverwrite = (file) => {
    setOverwriteConfirmFile(file);
  };

  const confirmResyncOverwrite = async () => {
    if (!overwriteConfirmFile) return;
    const file = overwriteConfirmFile;
    setOverwriteConfirmFile(null);
    setResyncingId(file.payment_file_id);
    try {
      const { data } = await api.post(`/payments/v2/files/${file.payment_file_id}/resync`, { force_overwrite: true }, {
        params: { account_id: currentAccount.id },
      });
      const totals = data?.sync_result?.totals || {};
      toast.success(`Sync & Overwrite completed for ${file.batch_name || file.payment_file_id} — all data refreshed from source`);
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync & Overwrite failed');
    } finally {
      setResyncingId(null);
    }
  };

  const handleActionComplete = () => {
    setSelectedPF(null);
    fetchFiles();
    fetchPaymentStats();
  };

  const handleDownloadCsv = async (file) => {
    try {
      const response = await api.get(`/payments/v2/files/${file.payment_file_id}/export-csv`, {
        params: { account_id: currentAccount.id },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${file.batch_name || file.payment_file_id}_utilities.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download CSV');
    }
  };

  // ─── BULK UPLOAD HANDLERS ───────────────────────────────
  const parseBulkCsv = async (file) => {
    const text = await file.text();
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim().toLowerCase());
    const billIdIdx = headers.findIndex(h => h.includes('bill id') || h === 'bill_id');
    const batchIdIdx = headers.findIndex(h => h.includes('batch id') || h === 'batch_id');
    const pmtDateIdx = headers.findIndex(h => h.includes('payment date') || h === 'payment_date');
    const amountIdx = headers.findIndex(h => h.includes('amount paid') || h === 'amount_paid');
    const feeIdx = headers.findIndex(h => h.includes('service fee') || h === 'service_fee');
    const refIdx = headers.findIndex(h => h.includes('transaction ref') || h === 'transaction_ref');
    const typeIdx = headers.findIndex(h => h.includes('transaction type') || h === 'transaction_type');
    const paidByIdx = headers.findIndex(h => h.includes('paid by') || h === 'paid_by');

    const cleanNum = (v) => {
      if (!v) return 0;
      return parseFloat(String(v).replace(/[$,"\s]/g, '')) || 0;
    };

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      // Handle CSV with quoted fields
      const parts = lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);
      if (!parts) continue;
      const vals = parts.map(p => p.replace(/^"|"$/g, '').trim());
      const billId = billIdIdx >= 0 ? vals[billIdIdx] : '';
      if (!billId || billId.length < 10) continue; // skip non-UUID rows

      rows.push({
        bill_id: billId,
        batch_id: batchIdIdx >= 0 ? (vals[batchIdIdx] || '') : '',
        payment_date: pmtDateIdx >= 0 ? (vals[pmtDateIdx] || '') : '',
        amount_paid: cleanNum(amountIdx >= 0 ? vals[amountIdx] : 0),
        service_fee: cleanNum(feeIdx >= 0 ? vals[feeIdx] : 0),
        transaction_ref: refIdx >= 0 ? (vals[refIdx] || '') : '',
        transaction_type: typeIdx >= 0 ? (vals[typeIdx] || '') : '',
        paid_by_name: paidByIdx >= 0 ? (vals[paidByIdx] || 'Manoj') : 'Manoj',
      });
    }
    return rows;
  };

  const handleBulkFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkFile(file);
    setBulkPreview(null);
    setBulkResult(null);

    try {
      const rows = await parseBulkCsv(file);
      setBulkParsedRows(rows);
      if (rows.length === 0) {
        toast.error('No valid rows found in CSV');
        return;
      }
      // Send preview request
      setBulkLoading(true);
      const { data } = await api.post('/payments/v2/bulk-upload-transactions/preview', {
        rows,
        account_id: null, // all orgs
        paid_by_name: 'Manoj',
      });
      setBulkPreview(data);
    } catch (err) {
      toast.error('Failed to parse/preview CSV');
      console.error(err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkCommit = async () => {
    if (!bulkParsedRows.length) return;
    setBulkCommitting(true);
    try {
      const { data } = await api.post('/payments/v2/bulk-upload-transactions/commit', {
        rows: bulkParsedRows,
        account_id: null,
        paid_by_name: 'Manoj',
        paid_by: user?.id || '',
      });
      setBulkResult(data);
      toast.success(`Bulk upload complete: ${data.success} transactions recorded`);
      fetchFiles();
      fetchPaymentStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkCommitting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/payments/v2/bulk-upload-transactions/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'payment_transactions_template.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  // ─── DETAIL VIEW ─────────────────────────────────────────
  if (selectedPF) {
    return (
      <PaymentFileDetail
        pfId={selectedPF.payment_file_id}
        accountId={currentAccount?.id}
        userId={user?.id}
        userName={user?.name}
        userRole={currentAccount?.role || (isSuperAdmin() ? 'admin' : 'viewer')}
        onBack={handleBack}
        onActionComplete={handleActionComplete}
      />
    );
  }

  // ─── LIST VIEW ───────────────────────────────────────────
  return (
    <MainLayout wide>
      <TooltipProvider delayDuration={200}>
      <div className="space-y-6" data-testid="payments-v2-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="h-6 w-6 text-foreground" strokeWidth={2} />
            <h1 className="text-2xl font-bold tracking-tight">Payments</h1>
          </div>
          <div className="flex items-center gap-2">
            {(isAdmin || isPfAdmin) && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5"
                onClick={() => { setBulkUploadOpen(true); setBulkFile(null); setBulkParsedRows([]); setBulkPreview(null); setBulkResult(null); }}
                data-testid="bulk-upload-transactions-btn"
              >
                <Upload className="h-3.5 w-3.5" />
                Upload Transactions
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4" data-testid="payments-summary-cards">
          {TABS.map(({ id, label }) => {
            const cfg = STATUS_CONFIG[id];
            const Icon = cfg.icon;
            const count = statusCounts[id] || 0;
            return (
              <button
                key={id}
                onClick={() => { setActiveTab(id); setSelectedPF(null); }}
                className={`flex items-center gap-4 p-4 border rounded-lg transition-all ${
                  activeTab === id
                    ? 'border-foreground/20 bg-muted/50 ring-1 ring-foreground/10'
                    : 'border-border hover:border-foreground/10 hover:bg-muted/30'
                }`}
                data-testid={`payments-card-${id}`}
              >
                <div className={`flex items-center justify-center h-10 w-10 rounded-md ${cfg.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="text-left">
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Aggregate Stats Bar */}
        {paymentStats && (paymentStats.review_count > 0 || paymentStats.approved_count > 0 || paymentStats.paid_count > 0) && (
          <div className="p-4 bg-muted/30 border border-border rounded-lg space-y-3" data-testid="payments-aggregate-stats">
          <div className="grid grid-cols-4 gap-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-3 cursor-help">
                    <div className="flex items-center justify-center h-9 w-9 rounded-md bg-blue-500/15 text-blue-600">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">Total Funds Requested <Info className="h-3 w-3 text-muted-foreground/60" /></p>
                      <p className="text-lg font-bold">{formatCurrency(paymentStats.total_funds_requested)}</p>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[280px] text-xs p-3">
                  <p>Sum of <code className="bg-black/20 px-1 rounded text-[10px] text-white font-mono">total_requested_amount</code> from ALL Payment Files (Review + Paid).</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-3 cursor-help" data-testid="kpi-funds-pulled">
                    <div className="flex items-center justify-center h-9 w-9 rounded-md bg-violet-500/15 text-violet-600">
                      <DollarSign className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">Funds Pulled <Info className="h-3 w-3 text-muted-foreground/60" /></p>
                      <p className="text-lg font-bold" data-testid="kpi-funds-pulled-value">{formatCurrency(paymentStats.total_funds_pulled)}</p>
                      {paymentStats.total_funds_pulled_bank > 0 && (
                        <p className="text-[10px] text-emerald-600 font-medium" data-testid="kpi-funds-pulled-bank-sub">
                          inc. {formatCurrency(paymentStats.total_funds_pulled_bank)} from bank statements
                        </p>
                      )}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[320px] text-xs p-3">
                  <p className="font-semibold mb-1">Funds Pulled</p>
                  <p className="mb-1">Sum of <strong>uploaded bank statement credits</strong> (Funds Pulled via Upload Credit File) + legacy <code className="bg-black/20 px-1 rounded text-[10px] text-white font-mono">pm_funds_pulled</code> ledger entries.</p>
                  <ul className="text-[10px] mt-1 space-y-0.5 text-muted-foreground">
                    <li>Bank statements: {formatCurrency(paymentStats.total_funds_pulled_bank || 0)} ({paymentStats.total_funds_pulled_bank_count || 0} lines)</li>
                    <li>Legacy ledger: {formatCurrency(paymentStats.total_funds_pulled_legacy || 0)}</li>
                  </ul>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-3 cursor-help">
                    <div className="flex items-center justify-center h-9 w-9 rounded-md bg-emerald-500/15 text-emerald-600">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">Total Paid <Info className="h-3 w-3 text-muted-foreground/60" /></p>
                      <p className="text-lg font-bold">{formatCurrency(paymentStats.total_paid)}</p>
                      {(paymentStats.paid_by_bp || 0) > 0 && (
                        <p className="text-[10px] text-muted-foreground font-medium">
                          BP: {formatCurrency(paymentStats.paid_by_bp)}{(paymentStats.paid_by_others || 0) > 0 && ` | Others: ${formatCurrency(paymentStats.paid_by_others)}`}
                        </p>
                      )}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[280px] text-xs p-3">
                  <p className="font-semibold mb-1">Total Paid</p>
                  <p className="mb-1">Sum of bill-level <code className="bg-black/20 px-1 rounded text-[10px] text-white font-mono">paid_amount</code> across ALL Payment Files.</p>
                  <p className="text-muted-foreground mt-1"><strong>Paid by BP</strong>: Payments processed by BillPortal.<br/><strong>Paid by Others</strong>: Payments by 3rd parties.</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-3 cursor-help" data-testid="refund-kpi-tile">
                    <div className="flex items-center justify-center h-9 w-9 rounded-md bg-red-500/15 text-red-600">
                      <ArrowLeft className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">Refunds <Info className="h-3 w-3 text-muted-foreground/60" /></p>
                      <div className="flex items-baseline gap-2.5">
                        <span className="text-sm font-bold text-amber-600" data-testid="kpi-to-refund">{formatCurrency(paymentStats.total_to_refund || 0)}</span>
                        <span className="text-[10px] text-amber-600/70 uppercase">To Refund</span>
                        <span className="text-muted-foreground/40">/</span>
                        <span className="text-sm font-bold text-emerald-600" data-testid="kpi-refunded">{formatCurrency(paymentStats.total_refunded || 0)}</span>
                        <span className="text-[10px] text-emerald-600/70 uppercase">Refunded</span>
                      </div>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[300px] text-xs p-3">
                  <p className="font-semibold mb-1">Refund Split</p>
                  <p className="mb-1"><strong>To Refund</strong>: refund recorded on a bill but not yet processed (refund_completed = false).</p>
                  <p><strong>Refunded</strong>: refund actually processed (refund_completed = true).</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Breakdown toggle */}
            <button
              onClick={() => setStatsBreakdownOpen(prev => !prev)}
              className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 transition-colors"
              data-testid="stats-breakdown-toggle"
            >
              {statsBreakdownOpen ? <ChevronLeft className="h-3 w-3 rotate-[-90deg]" /> : <ChevronRight className="h-3 w-3 rotate-90" />}
              {statsBreakdownOpen ? 'Hide' : 'Show'} breakdown by status
            </button>

            {statsBreakdownOpen && (
              <div className="border-t border-border pt-3 grid grid-cols-4 gap-4 text-xs animate-in fade-in slide-in-from-top-1 duration-200" data-testid="stats-breakdown-panel">
                {/* Requested breakdown — aligns under "Total Funds Requested" */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Requested</p>
                  {paymentStats.review_requested > 0 && (
                    <div className="flex justify-between"><span className="text-amber-600">Review</span><span className="font-mono">{formatCurrency(paymentStats.review_requested)}</span></div>
                  )}
                  {(paymentStats.approved_requested || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-blue-600">Approved</span><span className="font-mono">{formatCurrency(paymentStats.approved_requested)}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-emerald-600">Paid</span><span className="font-mono">{formatCurrency(paymentStats.paid_requested)}</span></div>
                </div>
                {/* Funds Pulled breakdown — aligns under "Funds Pulled" */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Funds Pulled</p>
                  {(paymentStats.approved_requested || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-blue-600">Approved</span><span className="font-mono">{formatCurrency(paymentStats.approved_requested)}</span></div>
                  )}
                  {paymentStats.total_funds_pulled_bank > 0 && (
                    <div className="flex justify-between" data-testid="breakdown-funds-pulled-bank">
                      <span className="text-emerald-700">Funds Pulled</span>
                      <span className="font-mono">{formatCurrency(paymentStats.total_funds_pulled_bank)}</span>
                    </div>
                  )}
                </div>
                {/* Payments breakdown — aligns under "Total Paid" */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Payments</p>
                  <div className="flex justify-between"><span className="text-emerald-600">Paid by BP</span><span className="font-mono">{formatCurrency(paymentStats.paid_by_bp || 0)}</span></div>
                  {(paymentStats.paid_by_others || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-violet-600">Paid by Others</span><span className="font-mono">{formatCurrency(paymentStats.paid_by_others || 0)}</span></div>
                  )}
                  {paymentStats.total_svc_fee > 0 && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Svc Fees</span><span className="font-mono">{formatCurrency(paymentStats.total_svc_fee)}</span></div>
                  )}
                </div>
                {/* Refunds breakdown — aligns under "Refunds" */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Refunds</p>
                  <div className="flex justify-between"><span className="text-amber-600">To Refund</span><span className="font-mono">{formatCurrency(paymentStats.total_to_refund || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-emerald-600">Refunded</span><span className="font-mono">{formatCurrency(paymentStats.total_refunded || 0)}</span></div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-border" data-testid="payments-tabs">
          <div className="flex gap-0">
            {TABS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === id
                    ? 'border-foreground text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`payments-tab-${id}`}
              >
                {label}
                {(statusCounts[id] || 0) > 0 && (
                  <span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded-full">
                    {statusCounts[id]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="payments-table-container">
          <PFTable
            files={files}
            loading={loading}
            activeTab={activeTab}
            onView={handleView}
            onDownloadCsv={handleDownloadCsv}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            issueStats={issueStats}
            pmtStats={pmtStats}
            isAdmin={isAdmin}
            isPfAdmin={isPfAdmin}
            onResync={handleResync}
            onResyncOverwrite={handleResyncOverwrite}
            resyncingId={resyncingId}
          />
        </div>

        {/* Sync & Overwrite Confirmation Dialog */}
        <Dialog open={!!overwriteConfirmFile} onOpenChange={(open) => { if (!open) setOverwriteConfirmFile(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="sync-overwrite-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertOctagon className="h-5 w-5" />
                Sync & Overwrite
              </DialogTitle>
              <DialogDescription className="pt-2 text-sm leading-relaxed">
                This will overwrite <strong>all manual changes</strong> for{' '}
                <strong>{overwriteConfirmFile?.batch_name || 'this payment file'}</strong>{' '}
                with the latest data from the source system.
                <br /><br />
                Any updates to payment amounts, notes, service charges, and other manually
                edited fields will be <strong className="text-destructive">permanently erased</strong>.
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setOverwriteConfirmFile(null)} data-testid="sync-overwrite-cancel">
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmResyncOverwrite} data-testid="sync-overwrite-confirm">
                Overwrite All Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Bulk Upload Transactions Dialog */}
        <Dialog open={bulkUploadOpen} onOpenChange={(open) => { if (!open) setBulkUploadOpen(false); }}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="bulk-upload-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload Payment Transactions
              </DialogTitle>
              <DialogDescription className="pt-1 text-sm">
                Upload a CSV file with transaction details to record payments in bulk across all customers.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Template Download + File Upload */}
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={handleDownloadTemplate} data-testid="bulk-download-template">
                  <Download className="h-3.5 w-3.5" />
                  Download Template
                </Button>
                <div className="flex-1">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleBulkFileChange}
                    className="block w-full text-xs text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-border file:text-xs file:font-medium file:bg-muted file:text-foreground hover:file:bg-muted/70 file:cursor-pointer cursor-pointer"
                    data-testid="bulk-file-input"
                  />
                </div>
              </div>

              {bulkFile && <p className="text-xs text-muted-foreground">File: {bulkFile.name} ({bulkParsedRows.length} rows parsed)</p>}

              {bulkLoading && (
                <div className="flex items-center gap-2 py-4 justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Validating transactions...</span>
                </div>
              )}

              {/* Preview Results */}
              {bulkPreview && !bulkResult && (
                <div className="space-y-3">
                  <div className="grid grid-cols-4 gap-3">
                    <div className="p-3 rounded-lg border bg-muted/30 text-center">
                      <p className="text-lg font-bold">{bulkPreview.total_rows}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">Total Rows</p>
                    </div>
                    <div className="p-3 rounded-lg border bg-emerald-50 text-center">
                      <p className="text-lg font-bold text-emerald-700">{bulkPreview.matched}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">Matched</p>
                    </div>
                    <div className="p-3 rounded-lg border bg-amber-50 text-center">
                      <p className="text-lg font-bold text-amber-700">{bulkPreview.already_paid}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">Already Paid</p>
                    </div>
                    <div className="p-3 rounded-lg border bg-red-50 text-center">
                      <p className="text-lg font-bold text-red-700">{bulkPreview.unmatched}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">Unmatched</p>
                    </div>
                  </div>

                  {bulkPreview.customers_affected?.length > 0 && (
                    <div className="text-xs">
                      <span className="font-medium">Customers affected: </span>
                      <span className="text-muted-foreground">{bulkPreview.customers_affected.join(', ')}</span>
                    </div>
                  )}

                  {bulkPreview.matched > 0 && (
                    <div className="max-h-48 overflow-y-auto border rounded-md">
                      <table className="w-full text-[11px]">
                        <thead className="bg-muted/40 sticky top-0">
                          <tr>
                            <th className="px-2 py-1.5 text-left font-semibold">Bill ID</th>
                            <th className="px-2 py-1.5 text-left font-semibold">Customer</th>
                            <th className="px-2 py-1.5 text-right font-semibold">Amount</th>
                            <th className="px-2 py-1.5 text-right font-semibold">Fee</th>
                            <th className="px-2 py-1.5 text-left font-semibold">Ref</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkPreview.matched_details?.map((m, i) => (
                            <tr key={i} className="border-t border-border/30">
                              <td className="px-2 py-1 font-mono truncate max-w-[120px]" title={m.bill_id}>{m.bill_id.slice(0, 8)}...</td>
                              <td className="px-2 py-1 truncate max-w-[120px]">{m.customer_name}</td>
                              <td className="px-2 py-1 text-right font-mono">{formatCurrency(m.new_amount)}</td>
                              <td className="px-2 py-1 text-right font-mono">{formatCurrency(m.service_fee)}</td>
                              <td className="px-2 py-1 truncate max-w-[80px]">{m.transaction_ref || '\u2014'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {bulkPreview.unmatched > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-red-600 font-medium">
                        {bulkPreview.unmatched} unmatched bills (click to expand)
                      </summary>
                      <div className="mt-1 max-h-32 overflow-y-auto p-2 bg-red-50 rounded text-[10px]">
                        {bulkPreview.unmatched_details?.map((u, i) => (
                          <p key={i} className="font-mono">{u.bill_id} — {u.reason}</p>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Commit Result */}
              {bulkResult && (
                <div className="p-4 rounded-lg border bg-emerald-50 space-y-2" data-testid="bulk-upload-result">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <p className="font-semibold text-emerald-700">Upload Complete</p>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center text-xs">
                    <div>
                      <p className="text-lg font-bold text-emerald-700">{bulkResult.success}</p>
                      <p className="text-muted-foreground">Recorded</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-amber-600">{bulkResult.skipped}</p>
                      <p className="text-muted-foreground">Skipped</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-red-600">{bulkResult.errors?.length || 0}</p>
                      <p className="text-muted-foreground">Errors</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setBulkUploadOpen(false)} data-testid="bulk-upload-cancel">
                {bulkResult ? 'Close' : 'Cancel'}
              </Button>
              {bulkPreview && !bulkResult && bulkPreview.matched > 0 && (
                <Button
                  onClick={handleBulkCommit}
                  disabled={bulkCommitting}
                  data-testid="bulk-upload-commit"
                >
                  {bulkCommitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : `Record ${bulkPreview.matched} Transactions`}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
      </TooltipProvider>
    </MainLayout>
  );
}
